import { asNr, date, email, maxLength, phone, required, type ValidationRuleObject } from '@staff/shared'
import type { CitizenshipEntry, TaxDomicileEntry } from '@staff/ui'

import type {
  KycAnswers,
  KycEmploymentStatus,
  KycExtraAmortizationFrequency,
  KycExtraAmortizationVolume,
  KycPepStatus,
  KycQuestionKey,
  KycSuggestedAnswers,
} from '@/services/kyc'

export const KYC_EMPLOYMENT_STATUSES: readonly KycEmploymentStatus[] = [
  'employed',
  'retired',
  'unemployed',
  'selfEmployed',
  'trialEmployed',
  'hourly',
  'temporarilyEmployed',
  'student',
]

/** The statuses with no employer to name: the employer question is neither shown nor sent for them. */
export const NO_EMPLOYER_STATUSES: readonly KycEmploymentStatus[] = ['retired', 'unemployed', 'student']

export const MAX_EMPLOYER_LENGTH = 250

const isEmploymentStatus = (value: string): value is KycEmploymentStatus =>
  (KYC_EMPLOYMENT_STATUSES as readonly string[]).includes(value)

export const hasEmployer = (status: string): boolean =>
  isEmploymentStatus(status) && !NO_EMPLOYER_STATUSES.includes(status)

export const needsEmploymentEnd = (status: string): boolean => status === 'temporarilyEmployed'

/**
 * Borgo's customers are Swedish citizens: both country lists hold Sweden, its number the customer's
 * personnummer. The form shows no number for it, and the server refuses a list without it.
 */
export const SWEDEN = 'SE'
export const HIDDEN_ID_COUNTRIES: readonly string[] = [SWEDEN]

/** The form's own state: text inputs hold strings, the two country lists hold their API shape. */
export interface KycFormValues {
  mobilePhone: string
  email: string
  citizenships: CitizenshipEntry[]
  taxDomiciles: TaxDomicileEntry[]
  employmentStatus: string
  employmentStartYearMonth: string
  employmentEndYearMonth: string
  employer: string
  monthlyIncome: string
  extraAmortizationFrequency: string
  extraAmortizationVolume: string
  pepStatus: string
}

export const emptyKycFormValues = (): KycFormValues => ({
  mobilePhone: '',
  email: '',
  citizenships: [{ countryCode: 'SE', registrationId: '' }],
  taxDomiciles: [{ countryCode: 'SE', tin: '', isPrimary: true }],
  employmentStatus: '',
  employmentStartYearMonth: '',
  employmentEndYearMonth: '',
  employer: '',
  monthlyIncome: '',
  extraAmortizationFrequency: '',
  extraAmortizationVolume: '',
  pepStatus: '',
})

export interface KycFormPrefill {
  values: KycFormValues
  /** When some answer came from an earlier cycle: when that cycle's answers were received. */
  suggestionReceivedAt: string | null
  /** Which of income and employer came from the distributor's latest application. */
  fromApplication: { monthlyIncome: boolean; employer: boolean }
}

/** Sweden in both lists with the customer's personnummer, added last when a list has no Sweden. */
export const withSwedishNumbers = (values: KycFormValues, personalNumber: string): KycFormValues => {
  const hasSweden = (entries: { countryCode: string }[]) => entries.some((entry) => entry.countryCode === SWEDEN)
  return {
    ...values,
    citizenships: hasSweden(values.citizenships)
      ? values.citizenships.map((entry) =>
          entry.countryCode === SWEDEN ? { ...entry, registrationId: personalNumber } : entry,
        )
      : [...values.citizenships, { countryCode: SWEDEN, registrationId: personalNumber }],
    taxDomiciles: hasSweden(values.taxDomiciles)
      ? values.taxDomiciles.map((entry) => (entry.countryCode === SWEDEN ? { ...entry, tin: personalNumber } : entry))
      : [
          ...values.taxDomiciles,
          { countryCode: SWEDEN, tin: personalNumber, isPrimary: values.taxDomiciles.length === 0 },
        ],
  }
}

/**
 * The form's starting values, per question, first match wins: the cycle's own answers, the suggestion
 * from an earlier cycle, for income and employer only this distributor's latest application, else
 * Sweden in both country lists and empty fields. Sweden's numbers are always the customer's personnummer.
 */
export const prefillKycForm = (
  cycleAnswers: Partial<KycAnswers>,
  suggested: KycSuggestedAnswers | null,
  personalNumber: string,
): KycFormPrefill => {
  const suggestion = suggested?.suggestion?.answers
  const application = suggested?.applicationValues
  const values = emptyKycFormValues()
  let usedSuggestion = false

  const pick = <TKey extends KycQuestionKey>(key: TKey): KycAnswers[TKey] | undefined => {
    if (cycleAnswers[key] !== undefined) return cycleAnswers[key]
    if (suggestion?.[key] !== undefined) {
      usedSuggestion = true
      return suggestion[key]
    }
    return undefined
  }

  values.mobilePhone = pick('mobilePhone') ?? ''
  values.email = pick('email') ?? ''
  values.citizenships = pick('citizenships') ?? values.citizenships
  values.taxDomiciles = pick('taxDomiciles') ?? values.taxDomiciles

  const employment = pick('employmentDetails')
  values.employmentStatus = employment?.employmentStatus ?? ''
  values.employmentStartYearMonth = employment?.employmentStartYearMonth ?? ''
  values.employmentEndYearMonth = employment?.employmentEndYearMonth ?? ''

  values.extraAmortizationFrequency = pick('extraAmortizationFrequency') ?? ''
  values.extraAmortizationVolume = pick('extraAmortizationVolume') ?? ''
  values.pepStatus = pick('pepStatus') ?? ''

  const fromApplication = { monthlyIncome: false, employer: false }

  const income = pick('monthlyIncome')
  if (income !== undefined) {
    values.monthlyIncome = String(income)
  } else if (application?.monthlyIncome != null) {
    values.monthlyIncome = String(application.monthlyIncome)
    fromApplication.monthlyIncome = true
  }

  const employer = pick('employer')
  if (employer !== undefined) {
    values.employer = employer
  } else if (application?.employer) {
    values.employer = application.employer
    fromApplication.employer = true
  }

  // A pre-filled status without an employer drops whatever employer the other sources had.
  if (values.employmentStatus && !hasEmployer(values.employmentStatus)) {
    values.employer = ''
    fromApplication.employer = false
  }

  return {
    values: withSwedishNumbers(values, personalNumber),
    suggestionReceivedAt: usedSuggestion ? (suggested?.suggestion?.answersReceivedAt ?? null) : null,
    fromApplication,
  }
}

/** Switching to a status without an employer clears the employer; any status but `temporarilyEmployed` clears the end month. */
export const withEmploymentStatus = (values: KycFormValues, employmentStatus: string): KycFormValues => ({
  ...values,
  employmentStatus,
  employer: hasEmployer(employmentStatus) ? values.employer : '',
  employmentEndYearMonth: needsEmploymentEnd(employmentStatus) ? values.employmentEndYearMonth : '',
})

/** The whole set, as the form sends it. Call only once the form validates. */
export const toKycAnswers = (values: KycFormValues): KycAnswers => {
  const employmentStatus = values.employmentStatus as KycEmploymentStatus
  return {
    mobilePhone: values.mobilePhone.trim(),
    email: values.email.trim(),
    citizenships: values.citizenships.map((entry) => ({
      countryCode: entry.countryCode,
      registrationId: entry.registrationId.trim(),
    })),
    taxDomiciles: values.taxDomiciles.map((entry) => ({
      countryCode: entry.countryCode,
      tin: entry.tin.trim(),
      isPrimary: entry.isPrimary,
    })),
    employmentDetails: {
      employmentStatus,
      employmentStartYearMonth: values.employmentStartYearMonth,
      ...(needsEmploymentEnd(employmentStatus) ? { employmentEndYearMonth: values.employmentEndYearMonth } : {}),
    },
    ...(hasEmployer(employmentStatus) ? { employer: values.employer.trim() } : {}),
    monthlyIncome: asNr(values.monthlyIncome),
    extraAmortizationFrequency: values.extraAmortizationFrequency as KycExtraAmortizationFrequency,
    extraAmortizationVolume: values.extraAmortizationVolume as KycExtraAmortizationVolume,
    pepStatus: values.pepStatus as KycPepStatus,
  }
}

const rule = (id: string, check: (value: unknown) => true | string): ValidationRuleObject => ({ id, rule: check })

const employmentStatusRule = rule(
  'kycEmploymentStatus',
  (value) => !value || isEmploymentStatus(String(value)) || 'Välj en anställningsform i listan.',
)

/** Gross income in whole kronor, zero or more. The digits may be grouped with spaces. */
const wholeKronorRule = rule('kycWholeKronor', (value) => {
  const text = String(value ?? '').replace(/\s/g, '')
  if (!text) return true
  if (text.startsWith('-')) return 'Inkomsten kan inte vara negativ.'
  return /^\d+$/.test(text) || 'Ange inkomsten i hela kronor, utan decimaler.'
})

/** The server's rules per field, with Swedish messages. The two country lists check their own. */
export const kycFieldRules = {
  mobilePhone: [required(), phone()],
  email: [required(), email()],
  employmentStatus: [required({ message: 'Välj en anställningsform.' }), employmentStatusRule],
  employmentStartYearMonth: [required(), date('YYYY-MM')],
  employmentEndYearMonth: [required({ message: 'Ange när visstidsanställningen slutar.' }), date('YYYY-MM')],
  employer: [
    required({ message: 'Arbetsgivare måste fyllas i för den här anställningsformen.' }),
    maxLength(MAX_EMPLOYER_LENGTH, `Högst ${MAX_EMPLOYER_LENGTH} tecken.`),
  ],
  monthlyIncome: [required(), wholeKronorRule],
  extraAmortizationFrequency: [required({ message: 'Välj ett alternativ.' })],
  extraAmortizationVolume: [required({ message: 'Välj ett alternativ.' })],
  pepStatus: [required({ message: 'Välj ett alternativ.' })],
} satisfies Partial<Record<keyof KycFormValues, ValidationRuleObject[]>>

/**
 * Splits the server's 400 message (`✖ <message>` lines, each followed by `→ at answers.<path>`) per question.
 * Messages without a question path go under `other`.
 */
export const serverMessagesByQuestion = (
  message: string,
): { byQuestion: Partial<Record<KycQuestionKey, string[]>>; other: string[] } => {
  const byQuestion: Partial<Record<KycQuestionKey, string[]>> = {}
  const other: string[] = []

  for (const block of message.split('✖').slice(1)) {
    const [text, at] = block.split('→ at')
    const question = at?.trim().match(/^answers\.(\w+)/)?.[1] as KycQuestionKey | undefined
    if (question) {
      ;(byQuestion[question] ??= []).push(text.trim())
    } else {
      other.push(text.trim())
    }
  }

  return { byQuestion, other }
}
